/**
 * StudentDetailsModal - Comprehensive student information display and editing modal
 * 
 * This component provides a detailed view of student information including:
 * - Personal details (name, gender, address, ID, birthday, join date)
 * - Group assignments
 * - Training information (belt level, weight)
 * - Parent contact information
 * - Attendance statistics and charts
 * 
 * Features:
 * - Edit mode for updating student information
 * - Attendance tracking with time-based filtering
 * - Visual charts for attendance analysis
 * - Parent communication shortcuts (WhatsApp, Email)
 * - Student disable/archive functionality
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Modal open state
 * @param {Function} props.onClose - Modal close handler
 * @param {Object} props.student - Student data object
 * @param {Array} props.groups - Available groups data
 * @param {boolean} props.isEditMode - Edit mode state
 * @param {Object} props.editedStudent - Student data being edited
 * @param {Function} props.onEdit - Edit mode handler
 * @param {Function} props.onSave - Save changes handler
 * @param {Function} props.onCancel - Cancel edit handler
 * @param {Function} props.onDisable - Disable student handler
 * @param {boolean} props.hasEditPrivilege - User edit permission
 * @param {Function} props.handleFieldChange - Field change handler
 * @param {Function} props.handleSpecialFieldChange - Special field change handler
 * @param {Array} props.beltColorOptions - Available belt colors
 * @param {boolean} props.disableLoading - Disable operation loading state
 * @param {boolean} props.showDisableConfirm - Show disable confirmation
 * @param {Function} props.setShowDisableConfirm - Set disable confirmation state
 * @param {Function} props.handleDisableStudent - Handle student disable
 * @param {Array} props.presenceLogs - Attendance logs data
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Badge, Form } from "react-bootstrap";
import CustomModal from "../../../Global/Modal/CustomModal";
import {
  FaUser,
  FaMapMarkerAlt,
  FaIdCard,
  FaBirthdayCake,
  FaCalendarAlt,
  FaUsers,
  FaMedal,
  FaWeight,
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaEdit,
  FaSave,
  FaTimes,
  FaUserSlash,
  FaCamera,
  FaChartPie,
  FaExclamationTriangle,
} from "react-icons/fa";
import Select from "react-select";
import { convertToDate } from "../../../../backend/timeFunctions";
import DatePicker from "react-datepicker";
import "../../../../styles/react-datepicker.css";
import { he } from "date-fns/locale";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

/**
 * Calculate age from birthday timestamp with robust error handling
 * @param {Object|string|number} birthday - Birthday timestamp or date
 * @returns {number|null} Age in years or null if invalid
 */
const getAge = (birthday) => {
  try {
    // Handle different birthday formats
    if (!birthday) return null;

    let birthDate;
    if (birthday.seconds) {
      birthDate = new Date(birthday.seconds * 1000);
    } else if (birthday.toDate && typeof birthday.toDate === 'function') {
      birthDate = birthday.toDate();
    } else if (typeof birthday === 'string' || typeof birthday === 'number') {
      birthDate = new Date(birthday);
    } else {
      return null;
    }

    // Validate date
    if (isNaN(birthDate.getTime())) return null;

    const currentDate = new Date();
    let age = currentDate.getFullYear() - birthDate.getFullYear();

    // Adjust age if birthday hasn't occurred this year
    if (
      currentDate.getMonth() < birthDate.getMonth() ||
      (currentDate.getMonth() === birthDate.getMonth() &&
        currentDate.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    // Validate age is reasonable
    return age >= 0 && age <= 120 ? age : null;
  } catch (error) {
    console.warn('Error calculating age:', error);
    return null;
  }
};

/**
 * Format time difference in Hebrew with robust error handling
 * @param {Object|string|number} fromDate - Start date
 * @param {Date} toDate - End date (defaults to current date)
 * @returns {string} Formatted time difference in Hebrew
 */
function formatHebrewTimeAgo(fromDate, toDate = new Date()) {
  try {
    if (!fromDate) return "";

    // Normalize fromDate to Date object
    let normalizedFromDate;
    if (typeof fromDate === "object" && fromDate.seconds) {
      normalizedFromDate = new Date(fromDate.seconds * 1000);
    } else if (fromDate.toDate && typeof fromDate.toDate === 'function') {
      normalizedFromDate = fromDate.toDate();
    } else if (typeof fromDate === "string" || typeof fromDate === "number") {
      normalizedFromDate = new Date(fromDate);
    } else {
      return "";
    }

    // Validate dates
    if (isNaN(normalizedFromDate.getTime()) || isNaN(toDate.getTime())) {
      return "";
    }

    // Ensure toDate is after fromDate
    if (normalizedFromDate > toDate) {
      return "";
    }

    let years = toDate.getFullYear() - normalizedFromDate.getFullYear();
    let months = toDate.getMonth() - normalizedFromDate.getMonth();
    let days = toDate.getDate() - normalizedFromDate.getDate();

    // Adjust for negative days
    if (days < 0) {
      months--;
      const prevMonth = new Date(toDate.getFullYear(), toDate.getMonth(), 0);
      days += prevMonth.getDate();
    }

    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    // Add one month if we've passed the day of the month
    if (toDate.getDate() >= normalizedFromDate.getDate()) {
      months += 1;
      if (months === 12) {
        years += 1;
        months = 0;
      }
    }

    // Hebrew labels with proper pluralization
    const yearLabel = years === 1 ? "שנה" : "שנים";
    const monthLabel = months === 1 ? "חודש" : "חודשים";
    const dayLabel = days === 1 ? "יום" : "ימים";

    let parts = [];
    if (years > 0) parts.push(`${years} ${yearLabel}`);
    if (months > 0 && years < 5) parts.push(`${months} ${monthLabel}`);
    if (years === 0 && months === 0 && days > 0) {
      parts.push(`${days} ${dayLabel}`);
    }

    if (parts.length === 0) return "היום";
    return `לפני ${parts.join(" ו-")}`;
  } catch (error) {
    console.warn('Error formatting Hebrew time ago:', error);
    return "";
  }
}

/**
 * Get background color for belt level display with fallback
 * @param {string} beltColor - Belt color name
 * @returns {string} CSS background color
 */
const getBeltColorBackground = (beltColor) => {
  try {
    if (!beltColor || typeof beltColor !== 'string') {
      return "#f8f9fa"; // Default light gray
    }

    const colors = {
      white: "#f8f9fa",
      yellow: "#fff3cd",
      orange: "#ffe5d0",
      green: "#d4edda",
      blue: "#cce5ff",
      brown: "#e9ecef",
      black: "#343a40",
      // Add Hebrew equivalents
      לבן: "#f8f9fa",
      צהוב: "#fff3cd",
      כתום: "#ffe5d0",
      ירוק: "#d4edda",
      כחול: "#cce5ff",
      חום: "#e9ecef",
      שחור: "#343a40",
    };

    const normalizedColor = beltColor.toLowerCase().trim();
    return colors[normalizedColor] || "#f8f9fa";
  } catch (error) {
    console.warn('Error getting belt color background:', error);
    return "#f8f9fa";
  }
};

/**
 * Utility functions for safe data access and validation
 */

/**
 * Safely access nested object properties
 * @param {Object} obj - Object to access
 * @param {string} path - Property path (e.g., 'specialFields.beltLevel')
 * @param {*} defaultValue - Default value if property doesn't exist
 * @returns {*} Property value or default
 */
const safeGet = (obj, path, defaultValue = null) => {
  try {
    if (!obj || typeof obj !== 'object') return defaultValue;
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : defaultValue;
    }, obj);
  } catch (error) {
    console.warn('Error accessing object property:', error);
    return defaultValue;
  }
};

/**
 * Safely convert timestamp to Date object
 * @param {Object|string|number} timestamp - Timestamp to convert
 * @returns {Date|null} Date object or null if invalid
 */
const safeTimestampToDate = (timestamp) => {
  try {
    if (!timestamp) return null;

    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    } else if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  } catch (error) {
    console.warn('Error converting timestamp to date:', error);
    return null;
  }
};

/**
 * Validate and normalize student groups data
 * @param {Array|string} groupsIDs - Student group IDs
 * @returns {Array} Normalized array of group IDs
 */
const normalizeStudentGroups = (groupsIDs) => {
  try {
    if (!groupsIDs) return [];
    if (Array.isArray(groupsIDs)) return groupsIDs.filter(id => id && id.toString().trim());
    if (typeof groupsIDs === 'string' || typeof groupsIDs === 'number') {
      return [groupsIDs.toString()];
    }
    return [];
  } catch (error) {
    console.warn('Error normalizing student groups:', error);
    return [];
  }
};

/**
 * Validate phone number format for WhatsApp
 * @param {string} phoneNumber - Phone number to validate
 * @returns {string|null} Formatted number or null if invalid
 */
const validatePhoneNumber = (phoneNumber) => {
  try {
    if (!phoneNumber || typeof phoneNumber !== 'string') return null;

    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');

    // Israeli phone number validation (starts with 05 or 972)
    if (cleaned.length >= 9 && cleaned.length <= 12) {
      if (cleaned.startsWith('05')) {
        return cleaned.slice(1); // Remove leading 0
      } else if (cleaned.startsWith('972')) {
        return cleaned.slice(3); // Remove country code
      }
    }

    return null;
  } catch (error) {
    console.warn('Error validating phone number:', error);
    return null;
  }
};

function StudentDetailsModal({
  isOpen,
  onClose,
  student,
  groups,
  isEditMode,
  editedStudent,
  onEdit,
  onSave,
  onCancel,
  onDisable,
  hasEditPrivilege,
  handleFieldChange,
  handleSpecialFieldChange,
  beltColorOptions,
  disableLoading,
  showDisableConfirm,
  setShowDisableConfirm,
  handleDisableStudent,
  presenceLogs,
}) {
  // State management with error handling
  const [imageError, setImageError] = useState(false);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("month");
  const [studentLineColor, setStudentLineColor] = useState("#1976d2");
  const [dataError, setDataError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset error states when student changes
  useEffect(() => {
    if (student) {
      setImageError(false);
      setDataError(null); // Reset data error when student changes
      setIsLoading(false); // Reset loading state when student changes
    }
  }, [student?.id]); // Use student.id instead of student?.imageURI to catch all student changes

  // Reset all error states when modal opens/closes or student changes
  useEffect(() => {
    if (isOpen && student) {
      // Reset all error states when modal opens with a new student
      setDataError(null);
      setIsLoading(false);
      setImageError(false);
    } else if (!isOpen) {
      // Reset all error states when modal closes
      setDataError(null);
      setIsLoading(false);
      setImageError(false);
    }
  }, [isOpen, student?.id]);

  // Reset image error when entering edit mode to ensure proper image display
  useEffect(() => {
    if (isEditMode) {
      setImageError(false);
    }
  }, [isEditMode]);

  // Calculate and set student line color based on monthly comparison data
  useEffect(() => {
    if (student && presenceLogs && Array.isArray(presenceLogs)) {
      const monthlyComparisonData = calculateMonthlyComparisonData();
      if (monthlyComparisonData.length > 0) {
        const monthsAboveAverage = monthlyComparisonData.filter(item => item.studentAttendance >= item.groupAverage).length;
        const totalMonths = monthlyComparisonData.length;
        const overallStudentColor = monthsAboveAverage >= totalMonths / 2 ? "#43a047" : "#f44336";
        setStudentLineColor(overallStudentColor);
      }
    }
  }, [student, presenceLogs, selectedTimeFilter]);

  /**
   * Calculate attendance data based on presence logs with robust error handling
   * @returns {Array} Attendance data array with present/missing counts
   */
  const calculateAttendanceData = useCallback(() => {
    try {
      // Validate input data - don't set error for missing data, just return empty result
      if (!student?.id) {
        return [
          { name: "נוכח", value: 0 },
          { name: "חסר", value: 0 },
        ];
      }

      if (!presenceLogs || !Array.isArray(presenceLogs)) {
        return [
          { name: "נוכח", value: 0 },
          { name: "חסר", value: 0 },
        ];
      }

      const now = new Date();
      let filterDate = new Date();

      // Set filter date based on selected time filter with validation
      switch (selectedTimeFilter) {
        case "month":
          filterDate.setMonth(now.getMonth() - 1);
          break;
        case "3months":
          filterDate.setMonth(now.getMonth() - 3);
          break;
        case "6months":
          filterDate.setMonth(now.getMonth() - 6);
          break;
        case "year":
          filterDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          filterDate.setMonth(now.getMonth() - 1);
      }

      let totalPresent = 0;
      let totalMissing = 0;

      // Normalize student groups
      const studentGroups = normalizeStudentGroups(student.groupsIDs);

      // Filter presence logs by time and student's groups
      const relevantLogs = presenceLogs.filter((log) => {
        try {
          if (!log || !log.groupID) return false;

          // Safely convert log time
          const logTime = safeTimestampToDate(log.logTime);
          if (!logTime || logTime < filterDate) return false;

          // Check if student belongs to the group of this log
          return studentGroups.includes(log.groupID);
        } catch (error) {
          console.warn('Error processing presence log:', error);
          return false;
        }
      });

      // Calculate attendance for each relevant log
      relevantLogs.forEach((log) => {
        try {
          const presenceData = Array.isArray(log.presenceLogDataForFront)
            ? log.presenceLogDataForFront
            : [];

          // Find student's presence record in this log
          const studentRecord = presenceData.find(
            (record) => record && record.studentID === student.id
          );

          if (studentRecord) {
            // Student was recorded in this log
            if (studentRecord.isPresent === true) {
              totalPresent++;
            } else {
              totalMissing++;
            }
          } else {
            // Student was not recorded in this log, count as missing
            totalMissing++;
          }
        } catch (error) {
          console.warn('Error calculating attendance for log:', error);
          totalMissing++; // Count as missing if there's an error
        }
      });

      return [
        { name: "נוכח", value: Math.max(0, totalPresent) },
        { name: "חסר", value: Math.max(0, totalMissing) },
      ];
    } catch (error) {
      console.error('Error calculating attendance data:', error);
      // Only set dataError for actual calculation errors, not for missing data
      if (student?.id && presenceLogs && Array.isArray(presenceLogs)) {
        console.warn('Setting dataError for student:', student.id, 'Error:', error);
        setDataError('שגיאה בחישוב נתוני נוכחות');
      }
      return [
        { name: "נוכח", value: 0 },
        { name: "חסר", value: 0 },
      ];
    }
  }, [student, presenceLogs, selectedTimeFilter]);

  // Validate required props after all hooks
  const hasRequiredProps = useMemo(() => {
    return student && typeof isOpen === 'boolean' && typeof onClose === 'function';
  }, [student, isOpen, onClose]);

  // Early return if missing required props
  if (!hasRequiredProps) {
    console.warn('StudentDetailsModal: Missing required props', { student, isOpen, onClose });
    return null;
  }

  const attendanceData = calculateAttendanceData();
  const hasAttendanceData = attendanceData.some((item) => item.value > 0);

  // Calculate student vs group average attendance
  const calculateGroupComparisonData = () => {
    if (!student || !presenceLogs || !Array.isArray(presenceLogs)) {
      return {
        studentAttendance: 0,
        groupAverage: 0,
        totalSessions: 0,
      };
    }

    const now = new Date();
    let filterDate = new Date();

    // Set filter date based on selected time filter
    switch (selectedTimeFilter) {
      case "month":
        filterDate.setMonth(now.getMonth() - 1);
        break;
      case "3months":
        filterDate.setMonth(now.getMonth() - 3);
        break;
      case "6months":
        filterDate.setMonth(now.getMonth() - 6);
        break;
      case "year":
        filterDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        filterDate.setMonth(now.getMonth() - 1);
    }

    const studentGroups = Array.isArray(student.groupsIDs)
      ? student.groupsIDs
      : [student.groupsIDs];

    let studentPresent = 0;
    let studentTotal = 0;
    let groupTotalPresent = 0;
    let groupTotalStudents = 0;

    // Filter presence logs by time and student's groups
    const relevantLogs = presenceLogs.filter((log) => {
      let logTime;
      if (log.logTime && typeof log.logTime.toDate === "function") {
        logTime = log.logTime.toDate();
      } else if (log.logTime && log.logTime.seconds) {
        logTime = new Date(log.logTime.seconds * 1000);
      } else {
        logTime = log.logTime;
      }

      // Check if log is within the selected time period
      if (!logTime || logTime < filterDate) return false;

      // Check if student belongs to the group of this log
      return studentGroups.includes(log.groupID);
    });

    // Calculate attendance for each relevant log
    relevantLogs.forEach((log) => {
      const presenceData = Array.isArray(log.presenceLogDataForFront)
        ? log.presenceLogDataForFront
        : [];

      // Count total students in this session (excluding the current student for group average)
      const sessionStudents = presenceData.length;
      let sessionPresent = 0;

      // Calculate group attendance for this session
      presenceData.forEach((record) => {
        if (record.isPresent) {
          sessionPresent++;
        }
      });

      // Add to group totals
      groupTotalPresent += sessionPresent;
      groupTotalStudents += sessionStudents;

      // Calculate student's attendance for this session
      const studentRecord = presenceData.find(
        (record) => record.studentID === student.id
      );

      studentTotal++;
      if (studentRecord && studentRecord.isPresent) {
        studentPresent++;
      }
    });

    const studentAttendance = studentTotal > 0 ? (studentPresent / studentTotal) * 100 : 0;
    const groupAverage = groupTotalStudents > 0 ? (groupTotalPresent / groupTotalStudents) * 100 : 0;

    return {
      studentAttendance: Math.round(studentAttendance * 10) / 10, // Round to 1 decimal
      groupAverage: Math.round(groupAverage * 10) / 10, // Round to 1 decimal
      totalSessions: studentTotal,
    };
  };

  const calculateMonthlyComparisonData = () => {
    if (!student || !presenceLogs || !Array.isArray(presenceLogs)) {
      return [];
    }

    const now = new Date();
    let filterDate = new Date();

    // Set filter date based on selected time filter
    switch (selectedTimeFilter) {
      case "month":
        filterDate.setMonth(now.getMonth() - 1);
        break;
      case "3months":
        filterDate.setMonth(now.getMonth() - 3);
        break;
      case "6months":
        filterDate.setMonth(now.getMonth() - 6);
        break;
      case "year":
        filterDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        filterDate.setMonth(now.getMonth() - 1);
    }

    const studentGroups = Array.isArray(student.groupsIDs)
      ? student.groupsIDs
      : [student.groupsIDs];

    // Filter presence logs by time and student's groups
    const relevantLogs = presenceLogs.filter((log) => {
      let logTime;
      if (log.logTime && typeof log.logTime.toDate === "function") {
        logTime = log.logTime.toDate();
      } else if (log.logTime && log.logTime.seconds) {
        logTime = new Date(log.logTime.seconds * 1000);
      } else {
        logTime = log.logTime;
      }

      if (!logTime || logTime < filterDate) return false;
      return studentGroups.includes(log.groupID);
    });

    // Group logs by month
    const monthlyData = {};

    relevantLogs.forEach((log) => {
      let logTime;
      if (log.logTime && typeof log.logTime.toDate === "function") {
        logTime = log.logTime.toDate();
      } else if (log.logTime && log.logTime.seconds) {
        logTime = new Date(log.logTime.seconds * 1000);
      } else {
        logTime = log.logTime;
      }

      const monthKey = `${logTime.getFullYear()}-${String(logTime.getMonth() + 1).padStart(2, '0')}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          studentPresent: 0,
          studentTotal: 0,
          groupPresent: 0,
          groupTotal: 0,
        };
      }

      const presenceData = Array.isArray(log.presenceLogDataForFront)
        ? log.presenceLogDataForFront
        : [];

      // Calculate group attendance for this session
      let sessionPresent = 0;
      presenceData.forEach((record) => {
        if (record.isPresent) {
          sessionPresent++;
        }
      });

      monthlyData[monthKey].groupPresent += sessionPresent;
      monthlyData[monthKey].groupTotal += presenceData.length;

      // Calculate student's attendance for this session
      const studentRecord = presenceData.find(
        (record) => record.studentID === student.id
      );

      monthlyData[monthKey].studentTotal++;
      if (studentRecord && studentRecord.isPresent) {
        monthlyData[monthKey].studentPresent++;
      }
    });

    // Convert to array and calculate percentages
    const monthlyComparisonData = Object.entries(monthlyData)
      .map(([monthKey, data]) => {
        const studentAttendance = data.studentTotal > 0
          ? (data.studentPresent / data.studentTotal) * 100
          : 0;
        const groupAverage = data.groupTotal > 0
          ? (data.groupPresent / data.groupTotal) * 100
          : 0;

        return {
          month: monthKey,
          studentAttendance: Math.round(studentAttendance * 10) / 10,
          groupAverage: Math.round(groupAverage * 10) / 10,
        };
      })
      .sort((a, b) => a.month.localeCompare(b.month))
      .filter(item => item.studentAttendance !== null && item.studentAttendance !== undefined);

    return monthlyComparisonData;
  };


  const monthlyComparisonData = calculateMonthlyComparisonData();
  const hasMonthlyComparisonData = monthlyComparisonData.length > 0;

  // Validate student data
  if (!student || typeof student !== 'object') {
    console.warn('StudentDetailsModal: Invalid student data', student);
    return null;
  }

  // Use edited student data if in edit mode, otherwise use original student data
  const currentStudent = isEditMode ? (editedStudent || student) : student;

  // Safely calculate age with error handling
  const age = getAge(currentStudent?.birthday);

  // Handle data errors
  if (dataError) {
    console.warn('StudentDetailsModal: Data error detected for student:', currentStudent?.id, 'Error:', dataError);
  }

  // Modern UI Styles
  const titleStyle = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#1976d2",
    borderRadius: 12,
    padding: "10px 18px",
    color: "#fff",
    fontWeight: 900,
    fontSize: "1.5rem",
    boxShadow: "0 2px 8px #1976d233",
  };

  const statusBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: "1.1rem",
    padding: "8px 8px",
    paddingBottm: "0",
    borderRadius: 20,
    fontWeight: 800,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    paddingLeft: "20px",
    paddingRight: "20px",
  };

  const infoBlock = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    flex: 1,
    fontWeight: 500,
    fontSize: "1.08em",
    color: "#222",
    background: "#f8fafc",
    borderRadius: 10,
    padding: "12px 16px",
    boxShadow: "0 1px 4px #1976d21a",
    margin: 0,
    minHeight: 48,
    maxWidth: "100%",
    overflow: "hidden",
  };

  const editInputStyle = {
    border: "1px solid #e0e0e0",
    background: "#fff",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: "inherit",
    fontWeight: "inherit",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    transition: "all 0.2s ease",
    width: "100%",
  };

  const labelStyle = {
    color: "#1976d2",
    fontWeight: 700,
    marginLeft: 4,
    whiteSpace: "nowrap",
  };

  const valueStyle = {
    color: "#222",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  const sectionTitleStyle = {
    fontWeight: 700,
    fontSize: "1.35rem",
    color: "#1976d2",
    borderRight: "6px solid #1976d2",
    paddingRight: 16,
    marginBottom: 18,
    display: "flex",
    alignItems: "center",
    gap: 10,
    direction: "rtl",
  };

  const actionButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "20px",
    borderRadius: "12px",
    fontWeight: "800",
    transition: "all 0.3s ease",
    border: "none",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    justifyContent: "center",
    margin: "0",
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        width={80}
        title={
          <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <div style={titleStyle}>
              <FaUser size={28} style={{ flexShrink: 0 }} />
              <span style={{ direction: "rtl" }}>
                {student?.name || "שם לא זמין"}
              </span>
            </div>
            <div
              style={{
                ...statusBadgeStyle,
                backgroundColor:
                  student?.status === "active" ? "#43a047" : "#ff9800",
                color: "#fff",
              }}
            >
              {student?.status === "active"
                ? "ספורטאי פעיל"
                : "ספוטראי ממתין לאישור"}
            </div>
          </div>
        }
        children={
          <div
            className="hebrewText"
            style={{
              direction: "rtl",
              fontSize: "1.1rem",
              color: "#222",
              minWidth: 320,
              maxWidth: 1200,
              margin: "0 auto",
              width: "100%",
              paddingBottom: 200,
            }}

          >
            {/* Action Buttons - Improved styling */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 16,
                marginBottom: 24,
              }}
            >
              {!isEditMode ? (
                hasEditPrivilege && (
                  <>
                    <Button
                      variant="primary"
                      onClick={onEdit}
                      style={{
                        ...actionButtonStyle,
                        backgroundColor: "#1976d2",
                        color: "#fff",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 4px 16px rgba(25,118,210,0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0,0,0,0.15)";
                      }}
                    >
                      ערוך
                      <FaEdit size={18} />
                    </Button>
                    {currentStudent?.status !== "not active" && (
                      <Button
                        variant="warning"
                        onClick={() => setShowDisableConfirm(true)}
                        disabled={disableLoading}
                        style={{
                          ...actionButtonStyle,
                          backgroundColor: "#ff9800",
                          color: "#fff",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 16px rgba(255,152,0,0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 2px 8px rgba(0,0,0,0.15)";
                        }}
                      >
                        השבת חניך
                        <FaUserSlash size={18} />
                      </Button>
                    )}
                  </>
                )
              ) : (
                <>
                  <Button
                    variant="secondary"
                    onClick={onCancel}
                    style={{
                      ...actionButtonStyle,
                      backgroundColor: "#6c757d",
                      color: "#fff",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 16px rgba(108,117,125,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.15)";
                    }}
                  >
                    ביטול
                    <FaTimes size={18} />
                  </Button>
                  <Button
                    variant="success"
                    onClick={onSave}
                    style={{
                      ...actionButtonStyle,
                      backgroundColor: "#43a047",
                      color: "#fff",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 16px rgba(67,160,71,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.15)";
                    }}
                  >
                    שמור
                    <FaSave size={18} />
                  </Button>
                </>
              )}
            </div>

            <div style={{ display: "flex", gap: "24px", direction: "ltr", paddingBottom: 24 }}>
              {/* Left Column - Image Only */}
              <div style={{ flex: "0 0 250px", direction: "rtl" }}>
                <div
                  style={{
                    background: "#f8fafc",
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 20,
                    boxShadow: "0 1px 4px #1976d21a",
                  }}
                >
                  {/* Image Section */}
                  <div style={{ marginBottom: 16 }}>
                    {(() => {
                      return currentStudent?.imageURI && !imageError;
                    })() ? (
                      <img
                        style={{
                          width: "100%",
                          height: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          marginBottom: 12,
                        }}
                        src={currentStudent.imageURI}
                        alt="תמונת החניך"
                        onError={() => {
                          setImageError(true);
                        }}
                        onLoad={() => {
                        }}
                      />
                    ) : null}
                    <div
                      style={{
                        width: "100%",
                        height: "200px",
                        background: "#e3e9f7",
                        borderRadius: "8px",
                        display: !currentStudent?.imageURI || imageError ? "flex" : "none",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 12,
                        color: "#9ca3af",
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        flexDirection: "column",
                      }}
                    >
                      <FaUser
                        size={80}
                        style={{
                          marginBottom: 12,
                          color: "#9ca3af"
                        }}
                      />
                      <div style={{ color: "#6b7280", fontSize: "0.9rem" }}>
                        תמונת החניך
                      </div>
                    </div>

                    {/* Image Upload Button - Now under the image */}
                    {isEditMode && (
                      <div style={{ marginBottom: 12 }}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            try {
                              const file = e.target.files[0];
                              if (file) {
                                // Validate file size (max 5MB)
                                if (file.size > 5 * 1024 * 1024) {
                                  alert('קובץ התמונה גדול מדי. הגודל המקסימלי הוא 5MB');
                                  return;
                                }

                                // Validate file type
                                if (!file.type.startsWith('image/')) {
                                  alert('הקובץ שנבחר אינו תמונה');
                                  return;
                                }

                                // Reset image error state when uploading new image
                                setImageError(false);

                                const imageUrl = URL.createObjectURL(file);
                                handleFieldChange("imageURI", imageUrl);
                              }
                            } catch (error) {
                              console.error('Error handling image upload:', error);
                              alert('שגיאה בטעינת התמונה');
                            }
                          }}
                          style={{ display: "none" }}
                          id="student-image-upload"
                        />
                        <label
                          htmlFor="student-image-upload"
                          style={{
                            display: "block",
                            cursor: "pointer",
                            padding: "12px",
                            backgroundColor: "#e3e9f7",
                            borderRadius: "8px",
                            textAlign: "center",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#d1d9f0";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#e3e9f7";
                          }}
                        >
                          <FaCamera
                            size={24}
                            style={{ marginBottom: 8, color: "#1976d2" }}
                          />
                          <div style={{ color: "#1976d2", fontWeight: 600 }}>
                            החלף תמונה
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Student Details */}
              <div style={{ flex: 1, direction: "rtl" }}>
                {/* Personal Information Section */}
                <div style={{ marginBottom: 24 }}>
                  <div style={sectionTitleStyle}>
                    <FaUser size={20} />
                    מידע אישי
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                    }}
                  >
                    {isEditMode ? (
                      <>
                        <div style={infoBlock}>
                          <FaUser color="#1976d2" size={20} />
                          <span style={labelStyle}>שם החניך:</span>
                          <Form.Control
                            type="text"
                            value={currentStudent?.name || ""}
                            onChange={(e) =>
                              handleFieldChange("name", e.target.value)
                            }
                            style={editInputStyle}
                          />
                        </div>
                        <div style={infoBlock}>
                          <FaUser color="#1976d2" size={20} />
                          <span style={labelStyle}>מין:</span>
                          <Form.Select
                            value={currentStudent?.gender || ""}
                            onChange={(e) =>
                              handleFieldChange("gender", e.target.value)
                            }
                            style={editInputStyle}
                          >
                            <option value="">בחר מין</option>
                            <option value="זכר">זכר</option>
                            <option value="נקבה">נקבה</option>
                          </Form.Select>
                        </div>
                        <div style={infoBlock}>
                          <FaMapMarkerAlt color="#1976d2" size={20} />
                          <span style={labelStyle}>כתובת:</span>
                          <Form.Control
                            type="text"
                            value={currentStudent?.address || ""}
                            onChange={(e) =>
                              handleFieldChange("address", e.target.value)
                            }
                            style={editInputStyle}
                          />
                        </div>
                        <div style={infoBlock}>
                          <FaIdCard color="#1976d2" size={20} />
                          <span style={labelStyle}>תעודת זהות:</span>
                          <Form.Control
                            type="text"
                            value={currentStudent?.IDnumber || ""}
                            onChange={(e) =>
                              handleFieldChange("IDnumber", e.target.value)
                            }
                            maxLength={9}
                            style={editInputStyle}
                          />
                        </div>
                        <div style={infoBlock}>
                          <FaBirthdayCake color="#1976d2" size={20} />
                          <span style={labelStyle}>יום הולדת:</span>
                          <DatePicker
                            selected={
                              currentStudent?.birthday?.seconds
                                ? new Date(currentStudent.birthday.seconds * 1000)
                                : null
                            }
                            onChange={(date) =>
                              handleFieldChange("birthday", {
                                seconds: date ? date.getTime() / 1000 : null,
                              })
                            }
                            dateFormat="yyyy-MM-dd"
                            className="eventFormInput form-control"
                            placeholderText="בחר תאריך"
                            isClearable
                            autoComplete="off"
                            locale={he}
                            style={{ direction: "rtl", textAlign: "center" }}
                          />
                        </div>
                        <div style={infoBlock}>
                          <FaCalendarAlt color="#1976d2" size={20} />
                          <span style={labelStyle}>תאריך הצטרפות:</span>
                          <span style={valueStyle}>
                            {currentStudent?.studentCreatedAt?.seconds
                              ? `${convertToDate(
                                currentStudent.studentCreatedAt.seconds,
                                0
                              )} (${formatHebrewTimeAgo(
                                currentStudent.studentCreatedAt
                              )})`
                              : "-"}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={infoBlock}>
                          <FaUser color="#1976d2" size={20} />
                          <span style={labelStyle}>שם החניך:</span>
                          <span style={valueStyle}>
                            {currentStudent?.name || "-"}
                          </span>
                        </div>
                        <div style={infoBlock}>
                          <FaUser color="#1976d2" size={20} />
                          <span style={labelStyle}>מין:</span>
                          <span style={valueStyle}>
                            {currentStudent?.gender || "-"}
                          </span>
                        </div>
                        <div style={infoBlock}>
                          <FaMapMarkerAlt color="#1976d2" size={20} />
                          <span style={labelStyle}>כתובת:</span>
                          <span style={valueStyle}>
                            {currentStudent?.address || "-"}
                          </span>
                        </div>
                        <div style={infoBlock}>
                          <FaIdCard color="#1976d2" size={20} />
                          <span style={labelStyle}>תעודת זהות:</span>
                          <span style={valueStyle}>
                            {currentStudent?.IDnumber || "-"}
                          </span>
                        </div>
                        <div style={infoBlock}>
                          <FaBirthdayCake color="#1976d2" size={20} />
                          <span style={labelStyle}>יום הולדת:</span>
                          <span style={valueStyle}>
                            {currentStudent?.birthday?.seconds
                              ? `${convertToDate(
                                currentStudent.birthday.seconds,
                                0
                              )}${age ? ` (${age} שנים)` : ""}`
                              : "-"}
                          </span>
                        </div>
                        <div style={infoBlock}>
                          <FaCalendarAlt color="#1976d2" size={20} />
                          <span style={labelStyle}>תאריך הצטרפות:</span>
                          <span style={valueStyle}>
                            {currentStudent?.studentCreatedAt?.seconds
                              ? `${convertToDate(
                                currentStudent.studentCreatedAt.seconds,
                                0
                              )} (${formatHebrewTimeAgo(
                                currentStudent.studentCreatedAt
                              )})`
                              : "-"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Groups Section - Now in Personal Information */}
                  <div style={{ marginTop: 24 }}>
                    <div style={sectionTitleStyle}>
                      <FaUsers size={20} />
                      קבוצות
                    </div>
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: "0 1px 4px #1976d21a",
                      }}
                    >
                      {isEditMode ? (
                        <Select
                          isMulti
                          value={groups?.filter((g) =>
                            currentStudent?.groupsIDs?.includes(g.id)
                          )}
                          options={groups}
                          getOptionLabel={(option) =>
                            `${option.name} - ${option.dojo}`
                          }
                          getOptionValue={(option) => option.id}
                          onChange={(selected) => {
                            handleFieldChange(
                              "groupsIDs",
                              selected ? selected.map((option) => option.id) : []
                            );
                          }}
                          placeholder="בחר קבוצות..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              direction: "rtl",
                              textAlign: "right",
                              borderRadius: 8,
                              border: "1px solid #e3e9f7",
                              minHeight: 48,
                            }),
                            menu: (base) => ({
                              ...base,
                              direction: "rtl",
                              textAlign: "right",
                            }),
                            multiValue: (base) => ({
                              ...base,
                              direction: "rtl",
                            }),
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 12,
                            direction: "rtl",
                            minHeight: 48,
                            alignItems: "center",
                          }}
                        >
                          {Array.isArray(currentStudent.groupsIDs)
                            ? currentStudent.groupsIDs
                              .map((groupID, index) => {
                                const group = groups?.find(
                                  (g) => g.id === groupID
                                );
                                if (group) {
                                  return (
                                    <Badge
                                      key={index}
                                      style={{
                                        background:
                                          "linear-gradient(45deg, #2196F3, #1976D2)",
                                        padding: "10px 18px",
                                        fontSize: "14px",
                                        borderRadius: "24px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                        display: "flex",
                                        alignItems: "center",
                                        transition: "transform 0.2s ease",
                                        cursor: "default",
                                        fontWeight: "500",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "scale(1.05)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "scale(1)";
                                      }}
                                    >
                                      {`${group.name} - ${group.dojo}`}
                                    </Badge>
                                  );
                                }
                                return null;
                              })
                              .filter(Boolean)
                            : (() => {
                              const group = groups?.find(
                                (g) => g.id === currentStudent.groupsIDs
                              );
                              if (group) {
                                return (
                                  <Badge
                                    style={{
                                      background:
                                        "linear-gradient(45deg, #2196F3, #1976D2)",
                                      padding: "10px 18px",
                                      fontSize: "14px",
                                      borderRadius: "24px",
                                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                      display: "flex",
                                      alignItems: "center",
                                      transition: "transform 0.2s ease",
                                      cursor: "default",
                                      fontWeight: "500",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = "scale(1.05)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = "scale(1)";
                                    }}
                                  >
                                    {`${group.name} - ${group.dojo}`}
                                  </Badge>
                                );
                              }
                              return null;
                            })()}
                          {Array.isArray(currentStudent.groupsIDs)
                            ? currentStudent.groupsIDs.length > 0 &&
                            !currentStudent.groupsIDs.some((id) =>
                              groups?.find((g) => g.id === id)
                            ) && (
                              <p
                                style={{
                                  color: "#6c757d",
                                  fontSize: "14px",
                                  margin: "8px 0 0 0",
                                  fontStyle: "italic",
                                }}
                              >
                                לא משויך לקבוצות
                              </p>
                            )
                            : !groups?.find(
                              (g) => g.id === currentStudent.groupsIDs
                            ) && (
                              <p
                                style={{
                                  color: "#6c757d",
                                  fontSize: "14px",
                                  margin: "8px 0 0 0",
                                  fontStyle: "italic",
                                }}
                              >
                                לא משויך לקבוצות
                              </p>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Training Information Section */}
                <div style={{ marginBottom: 24 }}>
                  <div style={sectionTitleStyle}>
                    <FaMedal size={20} />
                    נתונים נוספים
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                    }}
                  >
                    {isEditMode ? (
                      <>
                        <div style={infoBlock}>
                          <FaMedal color="#1976d2" size={20} />
                          <span style={labelStyle}>צבע חגורה:</span>
                          <Form.Select
                            value={
                              currentStudent?.specialFields?.beltLevel || ""
                            }
                            onChange={(e) =>
                              handleSpecialFieldChange(
                                "beltLevel",
                                e.target.value
                              )
                            }
                            style={editInputStyle}
                          >
                            <option value="">בחר צבע</option>
                            {beltColorOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                        <div style={infoBlock}>
                          <FaWeight color="#1976d2" size={20} />
                          <span style={labelStyle}>משקל (ק״ג):</span>
                          <Form.Control
                            type="number"
                            value={
                              currentStudent?.specialFields?.weightKG || ""
                            }
                            onChange={(e) =>
                              handleSpecialFieldChange(
                                "weightKG",
                                e.target.value
                              )
                            }
                            style={editInputStyle}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={infoBlock}>
                          <FaMedal color="#1976d2" size={20} />
                          <span style={labelStyle}>צבע חגורה:</span>
                          <span
                            style={{
                              ...valueStyle,
                              backgroundColor: currentStudent?.specialFields
                                ?.beltLevel
                                ? getBeltColorBackground(
                                  currentStudent.specialFields.beltLevel
                                )
                                : "transparent",
                              padding: currentStudent?.specialFields?.beltLevel
                                ? "4px 8px"
                                : 0,
                              borderRadius: currentStudent?.specialFields
                                ?.beltLevel
                                ? "4px"
                                : 0,
                            }}
                          >
                            {currentStudent?.specialFields?.beltLevel || "-"}
                          </span>
                        </div>
                        <div style={infoBlock}>
                          <FaWeight color="#1976d2" size={20} />
                          <span style={labelStyle}>משקל (ק״ג):</span>
                          <span style={valueStyle}>
                            {currentStudent?.specialFields?.weightKG || "-"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Parents Information Section */}
                <div>
                  <div style={sectionTitleStyle}>
                    <FaUsers size={20} />
                    פרטי הורים
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: 16,
                    }}
                  >
                    {/* Parent 1 */}
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: 12,
                        padding: 16,
                        boxShadow: "0 1px 4px #1976d21a",
                      }}
                    >
                      <h6
                        style={{
                          fontWeight: "bold",
                          marginBottom: 12,
                          fontSize: "1.1rem",
                          color: "#1976d2",
                        }}
                      >
                        הורה 1
                      </h6>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr",
                          gap: 12,
                        }}
                      >
                        {isEditMode ? (
                          <>
                            <div style={infoBlock}>
                              <FaUser color="#1976d2" size={20} />
                              <span style={labelStyle}>שם:</span>
                              <Form.Control
                                type="text"
                                value={currentStudent?.firstParentName || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    "firstParentName",
                                    e.target.value
                                  )
                                }
                                style={editInputStyle}
                              />
                            </div>
                            <div style={infoBlock}>
                              <FaPhone color="#1976d2" size={20} />
                              <span style={labelStyle}>טלפון:</span>
                              <Form.Control
                                type="tel"
                                value={currentStudent?.firstParentNumber || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    "firstParentNumber",
                                    e.target.value
                                  )
                                }
                                style={editInputStyle}
                              />
                            </div>
                            <div style={infoBlock}>
                              <FaEnvelope color="#1976d2" size={20} />
                              <span style={labelStyle}>אימייל:</span>
                              <Form.Control
                                type="email"
                                value={
                                  currentStudent?.firstParentEmailAddress || ""
                                }
                                onChange={(e) =>
                                  handleFieldChange(
                                    "firstParentEmailAddress",
                                    e.target.value
                                  )
                                }
                                style={editInputStyle}
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={infoBlock}>
                              <FaUser color="#1976d2" size={20} />
                              <span style={labelStyle}>שם:</span>
                              <span style={valueStyle}>
                                {currentStudent?.firstParentName || "-"}
                              </span>
                            </div>
                            <div style={infoBlock}>
                              <FaPhone color="#1976d2" size={20} />
                              <span style={labelStyle}>טלפון:</span>
                              <span style={valueStyle}>
                                {currentStudent?.firstParentNumber || "-"}
                              </span>
                              {currentStudent?.firstParentNumber && (
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => {
                                    try {
                                      const formattedNumber = validatePhoneNumber(currentStudent.firstParentNumber);
                                      if (formattedNumber) {
                                        window.open(
                                          `https://wa.me/+972${formattedNumber}`,
                                          "_blank"
                                        );
                                      } else {
                                        console.warn('Invalid phone number format for WhatsApp:', currentStudent.firstParentNumber);
                                        alert('מספר הטלפון אינו בפורמט תקין');
                                      }
                                    } catch (error) {
                                      console.error('Error opening WhatsApp:', error);
                                      alert('שגיאה בפתיחת WhatsApp');
                                    }
                                  }}
                                  style={{
                                    ...actionButtonStyle,
                                    backgroundColor: "#25D366",
                                    color: "#fff",
                                    width: "150px",
                                    fontSize: "14px",
                                    marginRight: "8px",
                                  }}
                                  title="פתח WhatsApp"
                                >
                                  <FaWhatsapp size={16} />
                                  WhatsApp
                                </Button>
                              )}
                            </div>
                            <div style={infoBlock}>
                              <FaEnvelope color="#1976d2" size={20} />
                              <span style={labelStyle}>אימייל:</span>
                              <span style={valueStyle}>
                                {currentStudent?.firstParentEmailAddress ||
                                  "-"}
                              </span>
                              {currentStudent?.firstParentEmailAddress && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => {
                                    try {
                                      const email = currentStudent.firstParentEmailAddress.trim();
                                      if (email && email.includes('@')) {
                                        window.open(
                                          `mailto:${email}`,
                                          "_blank"
                                        );
                                      } else {
                                        console.warn('Invalid email format:', email);
                                        alert('כתובת האימייל אינה תקינה');
                                      }
                                    } catch (error) {
                                      console.error('Error opening email:', error);
                                      alert('שגיאה בפתיחת האימייל');
                                    }
                                  }}
                                  style={{
                                    ...actionButtonStyle,
                                    backgroundColor: "#007bff",
                                    color: "#fff",
                                    width: "150px",
                                    fontSize: "14px",
                                    marginRight: "8px",
                                  }}
                                  title="שלח אימייל"
                                >
                                  <FaEnvelope size={14} />
                                  אימייל
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Parent 2 */}
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: 12,
                        padding: 16,
                        boxShadow: "0 1px 4px #1976d21a",
                      }}
                    >
                      <h6
                        style={{
                          fontWeight: "bold",
                          marginBottom: 12,
                          fontSize: "1.1rem",
                          color: "#1976d2",
                        }}
                      >
                        הורה 2
                      </h6>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr",
                          gap: 12,
                        }}
                      >
                        {isEditMode ? (
                          <>
                            <div style={infoBlock}>
                              <FaUser color="#1976d2" size={20} />
                              <span style={labelStyle}>שם:</span>
                              <Form.Control
                                type="text"
                                value={currentStudent?.secondParentName || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    "secondParentName",
                                    e.target.value
                                  )
                                }
                                style={editInputStyle}
                              />
                            </div>
                            <div style={infoBlock}>
                              <FaPhone color="#1976d2" size={20} />
                              <span style={labelStyle}>טלפון:</span>
                              <Form.Control
                                type="tel"
                                value={currentStudent?.secondParentNumber || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    "secondParentNumber",
                                    e.target.value
                                  )
                                }
                                style={editInputStyle}
                              />
                            </div>
                            <div style={infoBlock}>
                              <FaEnvelope color="#1976d2" size={20} />
                              <span style={labelStyle}>אימייל:</span>
                              <Form.Control
                                type="email"
                                value={
                                  currentStudent?.secondParentEmailAddress || ""
                                }
                                onChange={(e) =>
                                  handleFieldChange(
                                    "secondParentEmailAddress",
                                    e.target.value
                                  )
                                }
                                style={editInputStyle}
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={infoBlock}>
                              <FaUser color="#1976d2" size={20} />
                              <span style={labelStyle}>שם:</span>
                              <span style={valueStyle}>
                                {currentStudent?.secondParentName || "-"}
                              </span>
                            </div>
                            <div style={infoBlock}>
                              <FaPhone color="#1976d2" size={20} />
                              <span style={labelStyle}>טלפון:</span>
                              <span style={valueStyle}>
                                {currentStudent?.secondParentNumber || "-"}
                              </span>
                              {currentStudent?.secondParentNumber && (
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => {
                                    try {
                                      const formattedNumber = validatePhoneNumber(currentStudent.secondParentNumber);
                                      if (formattedNumber) {
                                        window.open(
                                          `https://wa.me/+972${formattedNumber}`,
                                          "_blank"
                                        );
                                      } else {
                                        console.warn('Invalid phone number format for WhatsApp:', currentStudent.secondParentNumber);
                                        alert('מספר הטלפון אינו בפורמט תקין');
                                      }
                                    } catch (error) {
                                      console.error('Error opening WhatsApp:', error);
                                      alert('שגיאה בפתיחת WhatsApp');
                                    }
                                  }}
                                  style={{
                                    ...actionButtonStyle,
                                    backgroundColor: "#25D366",
                                    color: "#fff",
                                    width: "150px",
                                    fontSize: "14px",
                                    marginRight: "8px",
                                  }}
                                  title="פתח WhatsApp"
                                >
                                  <FaWhatsapp size={14} />
                                  WhatsApp
                                </Button>
                              )}
                            </div>
                            <div style={infoBlock}>
                              <FaEnvelope color="#1976d2" size={20} />
                              <span style={labelStyle}>אימייל:</span>
                              <span style={valueStyle}>
                                {currentStudent?.secondParentEmailAddress ||
                                  "-"}
                              </span>
                              {currentStudent?.secondParentEmailAddress && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => {
                                    try {
                                      const email = currentStudent.secondParentEmailAddress.trim();
                                      if (email && email.includes('@')) {
                                        window.open(
                                          `mailto:${email}`,
                                          "_blank"
                                        );
                                      } else {
                                        console.warn('Invalid email format:', email);
                                        alert('כתובת האימייל אינה תקינה');
                                      }
                                    } catch (error) {
                                      console.error('Error opening email:', error);
                                      alert('שגיאה בפתיחת האימייל');
                                    }
                                  }}
                                  style={{
                                    ...actionButtonStyle,
                                    backgroundColor: "#007bff",
                                    color: "#fff",
                                    width: "150px",
                                    fontSize: "14px",
                                    marginRight: "8px",
                                  }}
                                  title="שלח אימייל"
                                >
                                  <FaEnvelope size={14} />
                                  אימייל
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistics Section */}
                {!isEditMode && <div style={{ marginTop: 24, marginBottom: 24 }}>
                  <div style={sectionTitleStyle}>
                    <FaChartPie size={20} />
                    נוכחות באימונים
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 16,
                    }}
                  >

                    {/* Time Filter Dropdown */}
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: "0 1px 4px #1976d21a",
                      }}
                    >
                      <h6
                        style={{
                          fontWeight: "bold",
                          marginBottom: 12,
                          fontSize: "1.1rem",
                          color: "#1976d2",
                        }}
                      >
                        סינון זמן
                      </h6>
                      <Form.Select
                        value={selectedTimeFilter}
                        onChange={(e) => setSelectedTimeFilter(e.target.value)}
                        style={editInputStyle}
                      >
                        <option value="month">חודש אחרון</option>
                        <option value="3months">3 חודשים אחרונים</option>
                        <option value="6months">6 חודשים אחרונים</option>
                        <option value="year">שנה אחרונה</option>
                      </Form.Select>
                      <div
                        style={{
                          marginTop: 12,
                          padding: 12,
                          background: "#e3f2fd",
                          borderRadius: 8,
                          fontSize: "0.9rem",
                          color: "#1976d2",
                        }}
                      >
                        <strong>סה"כ אימונים:</strong> {(() => {
                          try {
                            return attendanceData.reduce((sum, item) => sum + (item.value || 0), 0);
                          } catch (error) {
                            console.warn('Error calculating total sessions:', error);
                            return 0;
                          }
                        })()}
                        <br />
                        <strong>נוכחויות:</strong> {(() => {
                          try {
                            return attendanceData[0]?.value || 0;
                          } catch (error) {
                            console.warn('Error getting present count:', error);
                            return 0;
                          }
                        })()}
                        <br />
                        <strong>חיסורים:</strong> {(() => {
                          try {
                            return attendanceData[1]?.value || 0;
                          } catch (error) {
                            console.warn('Error getting missing count:', error);
                            return 0;
                          }
                        })()}
                      </div>
                    </div>

                    {/* Attendance Pie Chart */}
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: "0 1px 4px #1976d21a",
                      }}
                    >
                      <h6
                        style={{
                          fontWeight: "bold",
                          marginBottom: 12,
                          fontSize: "1.1rem",
                          color: "#1976d2",
                        }}
                      >
                        נוכחות באימונים
                      </h6>
                      {!hasAttendanceData || dataError ? (
                        <div
                          style={{
                            height: 200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#636e72",
                            fontSize: "1rem",
                            textAlign: "center",
                            padding: "20px",
                            flexDirection: "column",
                          }}
                        >
                          {dataError ? (
                            <>
                              <FaExclamationTriangle size={24} style={{ marginBottom: 8, color: "#ff9800" }} />
                              <div>{dataError}</div>
                            </>
                          ) : (
                            "אין נתוני נוכחות זמינים לתקופה הנבחרת"
                          )}
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={attendanceData}
                              cx="50%"
                              cy="50%"
                              innerRadius={30}
                              outerRadius={80}
                              fill="#8884d8"
                              paddingAngle={5}
                              dataKey="value"
                              labelLine={false}
                              label={({
                                cx,
                                cy,
                                midAngle,
                                innerRadius,
                                outerRadius,
                                value,
                              }) => {
                                try {
                                  const RADIAN = Math.PI / 180;
                                  const radius =
                                    innerRadius + (outerRadius - innerRadius) * 0.5;
                                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                  const y = cy + radius * Math.sin(-midAngle * RADIAN);

                                  return (
                                    <text
                                      x={x}
                                      y={y}
                                      fill="white"
                                      textAnchor="middle"
                                      dominantBaseline="middle"
                                      fontSize={18}
                                      fontWeight="bold"
                                    >
                                      {value || 0}
                                    </text>
                                  );
                                } catch (error) {
                                  console.warn('Error rendering pie chart label:', error);
                                  return null;
                                }
                              }}
                            >
                              {attendanceData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={index === 0 ? "#43a047" : "red"}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "none",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                fontSize: "20px",
                              }}
                            />
                            <Legend
                              iconSize={10}
                              layout="horizontal"
                              verticalAlign="bottom"
                              align="center"
                              fontSize="34px"
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                    {/* Student vs Group Average Comparison Chart */}
                    <div
                      style={{
                        background: "#f8fafc",
                        borderRadius: 12,
                        padding: 12,
                        boxShadow: "0 1px 4px #1976d21a",
                        marginTop: 16,
                        paddingBottom: 12,
                        gridColumn: "1 / -1", // Span both columns
                        maxHeight: 300,
                      }}
                    >
                      <h6
                        style={{
                          fontWeight: "bold",
                          marginBottom: 12,
                          fontSize: "1.1rem",
                          color: "#1976d2",
                        }}
                      >
                        נוכחות הספורטאי בהשוואה לקבוצה
                      </h6>
                      {!hasMonthlyComparisonData || dataError ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#636e72",
                            fontSize: "1rem",
                            textAlign: "center",
                            padding: "20px",
                            paddingBottom: 0,
                            flexDirection: "column",
                          }}
                        >
                          {dataError ? (
                            <>
                              <FaExclamationTriangle size={24} style={{ marginBottom: 8, color: "#ff9800" }} />
                              <div>{dataError}</div>
                            </>
                          ) : (
                            "אין נתוני השוואה זמינים לתקופה הנבחרת"
                          )}
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={250}>
                          <LineChart
                            data={monthlyComparisonData || []}
                            margin={{ top: 5, right: 30, left: 20 }}
                            style={{ paddingBottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="month"
                              fontSize={12}
                              tick={{ fill: "grey" }}
                              tickFormatter={(value) => {
                                try {
                                  if (!value || typeof value !== 'string') return '';
                                  const [year, month] = value.split('-');
                                  if (!year || !month) return '';

                                  const monthNames = [
                                    'ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יוני',
                                    'יולי', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ'
                                  ];
                                  const monthIndex = parseInt(month) - 1;
                                  if (monthIndex < 0 || monthIndex >= monthNames.length) return '';

                                  return `${monthNames[monthIndex]} ${year}`;
                                } catch (error) {
                                  console.warn('Error formatting X-axis tick:', error);
                                  return '';
                                }
                              }}
                            />
                            <YAxis
                              domain={[0, 100]}
                              orientation="left"
                              fontSize={12}
                              tick={{ fill: "grey" }}
                              tickFormatter={(value) => `${value}%`}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "#fff",
                                border: "none",
                                borderRadius: "8px",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                fontSize: "14px",
                              }}
                              formatter={(value, name) => {
                                try {
                                  const formattedValue = typeof value === 'number' ? `${value}%` : '0%';
                                  const label = name === "studentAttendance" ? "חניך" : "ממוצע קבוצה";
                                  return [formattedValue, label];
                                } catch (error) {
                                  console.warn('Error formatting tooltip value:', error);
                                  return ['0%', 'לא ידוע'];
                                }
                              }}
                              labelFormatter={(value) => {
                                try {
                                  if (!value || typeof value !== 'string') return '';
                                  const [year, month] = value.split('-');
                                  if (!year || !month) return '';

                                  const monthNames = [
                                    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
                                    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
                                  ];
                                  const monthIndex = parseInt(month) - 1;
                                  if (monthIndex < 0 || monthIndex >= monthNames.length) return '';

                                  return `${monthNames[monthIndex]} ${year}`;
                                } catch (error) {
                                  console.warn('Error formatting tooltip label:', error);
                                  return '';
                                }
                              }}
                              labelStyle={{ color: "#1976d2" }}
                            />
                            <Legend
                              wrapperStyle={{ fontSize: "12px" }}
                              formatter={(value) => {
                                try {
                                  return value === "studentAttendance" ? "ממוצע ספורטאי" : "ממוצע קבוצה";
                                } catch (error) {
                                  console.warn('Error formatting legend:', error);
                                  return 'לא ידוע';
                                }
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="studentAttendance"
                              stroke={studentLineColor}
                              strokeWidth={3}
                              dot={{ fill: studentLineColor, strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6 }}
                              name="studentAttendance"
                              connectNulls={true}
                            />
                            <Line
                              type="monotone"
                              dataKey="groupAverage"
                              stroke="grey"
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={{ fill: "grey", strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6 }}
                              name="groupAverage"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                      {hasMonthlyComparisonData && (
                        <div
                          style={{
                            marginTop: 6,
                            padding: 12,
                            background: "#e3f2fd",
                            borderRadius: 8,
                            fontSize: "0.9rem",
                            color: "#1976d2",
                          }}
                        >
                          <strong>נתוני השוואה חודשיים</strong>
                          <br />
                          <strong>מספר חודשים:</strong> {monthlyComparisonData.length}
                          <br />
                          <strong>ממוצע נוכחות ספורטאי:</strong>{" "}
                          {(() => {
                            try {
                              if (!monthlyComparisonData || monthlyComparisonData.length === 0) return '0.0';
                              const sum = monthlyComparisonData.reduce((acc, item) => acc + (item.studentAttendance || 0), 0);
                              return (sum / monthlyComparisonData.length).toFixed(1);
                            } catch (error) {
                              console.warn('Error calculating student attendance average:', error);
                              return '0.0';
                            }
                          })()}%
                          <br />
                          <strong>ממוצע נוכחות בקבוצה:</strong>{" "}
                          {(() => {
                            try {
                              if (!monthlyComparisonData || monthlyComparisonData.length === 0) return '0.0';
                              const sum = monthlyComparisonData.reduce((acc, item) => acc + (item.groupAverage || 0), 0);
                              return (sum / monthlyComparisonData.length).toFixed(1);
                            } catch (error) {
                              console.warn('Error calculating group attendance average:', error);
                              return '0.0';
                            }
                          })()}%
                        </div>
                      )}
                    </div>
                  </div>
                </div>}
              </div>
            </div>
          </div>
        }
      />

      {/* Disable Confirmation Modal */}
      <CustomModal
        isOpen={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        title="השבתת חניך"
        width={35}
        height={null}
        footerButtons={[
          {
            title: "ביטול",
            style: "grey",
            onClick: () => setShowDisableConfirm(false),
            disabled: disableLoading,
          },
          {
            title: disableLoading ? "משבית..." : "השבת חניך",
            style: "red",
            onClick: handleDisableStudent,
            disabled: disableLoading,
          },
        ]}
      >
        <div style={{ direction: "rtl", fontSize: 17, textAlign: "center" }}>
          האם אתה בטוח שברצונך להשבית את החניך? פעולה זו תעביר אותו לארכיון.
        </div>
      </CustomModal>
    </>
  );
}

export default StudentDetailsModal;
